// client/src/pages/wholesaler/FlashOffers.js
// ================================================================
// WHOLESALEHUB FLASH OFFERS MANAGEMENT PAGE
// Wholesaler can create, manage, and view analytics for flash offers
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Plus, Clock, Eye, ShoppingCart, TrendingUp,
  Edit2, Trash2, XCircle, CheckCircle, Search,
  Calendar, Tag, AlertTriangle, ArrowUpCircle, X,
  Package, ChevronDown, ChevronUp
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../../services/api';
import { useTranslation } from 'react-i18next';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

// ================================================================
// CREATE / EDIT FLASH OFFER MODAL
// ================================================================
const FlashOfferModal = ({ offer, onClose, onSave }) => {
  const { t } = useTranslation(['wholesaler', 'common']);
  const [step, setStep] = useState(1); // 1=details, 2=products, 3=review
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(
    offer?.items?.map(i => i.productId) || []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customPrices, setCustomPrices] = useState(() => {
    if (offer?.discountType === 'fixed_price' && offer?.items) {
      const map = {};
      offer.items.forEach(i => { if (i.offerPrice) map[i.productId] = i.offerPrice; });
      return map;
    }
    return {};
  });

  const [formData, setFormData] = useState({
    title: offer?.title || '',
    description: offer?.description || '',
    discountType: offer?.discountType || 'percentage',
    discountPercentage: offer?.discountPercentage || 15,
    startDate: offer?.startDate
      ? new Date(offer.startDate).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    endDate: offer?.endDate
      ? new Date(offer.endDate).toISOString().slice(0, 16)
      : new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().slice(0, 16),
    targetAudience: offer?.targetAudience || 'all',
    minimumOrderAmount: offer?.minimumOrderAmount || 0,
    publishImmediately: true
  });

  // Fetch products
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${API_URL}/wholesaler/products`, {
        headers: getAuthHeaders(),
        params: { limit: 500, status: 'active' }
      });
      const prods = res.data.products || res.data || [];
      setProducts(Array.isArray(prods) ? prods : []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p._id));
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSelectedProductDetails = () => {
    return products.filter(p => selectedProducts.includes(p._id));
  };

  const calculateOfferPrice = (originalPrice, productId) => {
    if (formData.discountType === 'percentage') {
      return Math.round((originalPrice * (1 - formData.discountPercentage / 100)) * 100) / 100;
    }
    // fixed_price: use custom price if set
    if (productId && customPrices[productId] != null) {
      return Number(customPrices[productId]);
    }
    return originalPrice;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountPercentage: formData.discountType === 'percentage' ? formData.discountPercentage : null,
        customPrices: formData.discountType === 'fixed_price' ? customPrices : undefined,
        productIds: selectedProducts,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        targetAudience: formData.targetAudience,
        minimumOrderAmount: parseFloat(formData.minimumOrderAmount) || 0,
        publishImmediately: formData.publishImmediately
      };

      if (offer?._id) {
        await axios.put(`${API_URL}/flash-offers/${offer._id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_URL}/flash-offers`, payload, { headers: getAuthHeaders() });
      }

      onSave();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save flash offer';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-white" />
            <h2 className="text-xl font-bold text-white">
              {offer ? t('wholesaler:flashOffers.editOffer', 'Edit Flash Offer') : t('wholesaler:flashOffers.createOffer', 'Create Flash Offer')}
            </h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-red-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex border-b">
          {[
            { num: 1, label: t('wholesaler:flashOffers.steps.details', 'Details') },
            { num: 2, label: t('wholesaler:flashOffers.steps.products', 'Products') },
            { num: 3, label: t('wholesaler:flashOffers.steps.review', 'Review') }
          ].map(s => (
            <button
              key={s.num}
              onClick={() => {
                if (s.num === 2 && !formData.title.trim()) return;
                if (s.num === 3 && selectedProducts.length === 0) return;
                setStep(s.num);
              }}
              className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
                step === s.num
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : step > s.num
                  ? 'border-green-400 text-green-600'
                  : 'border-transparent text-gray-400'
              }`}
            >
              {s.num}. {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* STEP 1: Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('wholesaler:flashOffers.form.title', 'Offer Title')} *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  placeholder={t('wholesaler:flashOffers.form.titlePlaceholder', 'e.g. Weekend Special - 20% Off Dairy')}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  maxLength={120}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.title.length}/120</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('wholesaler:flashOffers.form.description', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder={t('wholesaler:flashOffers.form.descriptionPlaceholder', 'Optional details about the offer...')}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={2}
                  maxLength={500}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('wholesaler:flashOffers.form.discountType', 'Discount Type')}
                  </label>
                  <select
                    value={formData.discountType}
                    onChange={e => setFormData({...formData, discountType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                  >
                    <option value="percentage">{t('wholesaler:flashOffers.form.percentage', 'Percentage Off')}</option>
                    <option value="fixed_price">{t('wholesaler:flashOffers.form.fixedPrice', 'Fixed Price')}</option>
                  </select>
                </div>
                {formData.discountType === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('wholesaler:flashOffers.form.discountAmount', 'Discount %')}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.discountPercentage}
                        onChange={e => setFormData({...formData, discountPercentage: Math.min(90, Math.max(1, parseInt(e.target.value) || 0))})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-8"
                        min={1} max={90}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">%</span>
                    </div>
                  </div>
                )}
                {formData.discountType === 'fixed_price' && (
                  <div className="flex items-end">
                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 w-full">
                      {t('wholesaler:flashOffers.form.fixedPriceHint', 'Set offer prices per product in the next step')}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('wholesaler:flashOffers.form.startDate', 'Start Date')}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={e => setFormData({...formData, startDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('wholesaler:flashOffers.form.endDate', 'End Date')}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={e => setFormData({...formData, endDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('wholesaler:flashOffers.form.audience', 'Target Audience')}
                  </label>
                  <select
                    value={formData.targetAudience}
                    onChange={e => setFormData({...formData, targetAudience: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                  >
                    <option value="all">{t('wholesaler:flashOffers.form.allCustomers', 'All Customers')}</option>
                    <option value="approved_only">{t('wholesaler:flashOffers.form.approvedOnly', 'Approved Businesses Only')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('wholesaler:flashOffers.form.minOrder', 'Min. Order (€)')}
                  </label>
                  <input
                    type="number"
                    value={formData.minimumOrderAmount}
                    onChange={e => setFormData({...formData, minimumOrderAmount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                    min={0} step={5}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 bg-orange-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="publishNow"
                  checked={formData.publishImmediately}
                  onChange={e => setFormData({...formData, publishImmediately: e.target.checked})}
                  className="w-4 h-4 text-red-600 rounded"
                />
                <label htmlFor="publishNow" className="text-sm text-orange-800">
                  <span className="font-medium">{t('wholesaler:flashOffers.form.publishNow', 'Publish immediately')}</span>
                  <span className="block text-xs text-orange-600">{t('wholesaler:flashOffers.form.publishNowDesc', 'Customers will be notified via email and in-app notification')}</span>
                </label>
              </div>
            </div>
          )}

          {/* STEP 2: Products */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {t('wholesaler:flashOffers.form.selectProducts', 'Select products to include in this offer')}
                </p>
                <button onClick={selectAll} className="text-sm text-red-600 hover:text-red-700 font-medium">
                  {selectedProducts.length === filteredProducts.length
                    ? t('common:deselectAll', 'Deselect All')
                    : t('common:selectAll', 'Select All')}
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={t('wholesaler:flashOffers.form.searchProducts', 'Search products...')}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5"
                />
              </div>

              {selectedProducts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  <Zap className="w-4 h-4 inline mr-1" />
                  {selectedProducts.length} {t('wholesaler:flashOffers.form.productsSelected', 'products selected')}
                  {formData.discountType === 'percentage' && (
                    <span className="ml-1">— {formData.discountPercentage}% {t('wholesaler:flashOffers.form.offEach', 'off each')}</span>
                  )}
                </div>
              )}

              {loadingProducts ? (
                <div className="text-center py-8 text-gray-400">
                  {t('common:loading', 'Loading...')}
                </div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {filteredProducts.map(product => {
                    const isSelected = selectedProducts.includes(product._id);
                    const offerPrice = calculateOfferPrice(product.pricing.unitPrice);
                    return (
                      <div
                        key={product._id}
                        onClick={() => toggleProduct(product._id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-red-400 bg-red-50 ring-1 ring-red-200'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-red-600 border-red-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>

                        {product.images?.[0] && (
                          <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.category}</p>
                        </div>

                        <div className="text-right flex-shrink-0">
                          {isSelected && formData.discountType === 'percentage' ? (
                            <div>
                              <p className="text-xs text-gray-400 line-through">€{product.pricing.unitPrice.toFixed(2)}</p>
                              <p className="text-sm font-bold text-red-600">€{offerPrice.toFixed(2)}</p>
                            </div>
                          ) : isSelected && formData.discountType === 'fixed_price' ? (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <span className="text-xs text-gray-400">€</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={product.pricing.unitPrice}
                                placeholder={product.pricing.unitPrice.toFixed(2)}
                                value={customPrices[product._id] ?? ''}
                                onChange={e => setCustomPrices(prev => ({ ...prev, [product._id]: e.target.value }))}
                                className="w-20 px-2 py-1 text-sm border border-red-300 rounded focus:ring-1 focus:ring-red-400 focus:outline-none text-right font-bold text-red-600"
                              />
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-gray-700">€{product.pricing.unitPrice.toFixed(2)}/{product.pricing.unit}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-4 border border-red-200">
                <h3 className="font-bold text-lg text-gray-900">{formData.title}</h3>
                {formData.description && <p className="text-sm text-gray-600 mt-1">{formData.description}</p>}

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="text-sm">
                    <span className="text-gray-500">{t('wholesaler:flashOffers.form.discount', 'Discount')}:</span>
                    <span className="ml-1 font-bold text-red-600">
                      {formData.discountType === 'percentage' ? `${formData.discountPercentage}%` : t('wholesaler:flashOffers.form.customPrices', 'Custom Prices')}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">{t('wholesaler:flashOffers.form.productsLabel', 'Products')}:</span>
                    <span className="ml-1 font-bold">{selectedProducts.length}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">{t('wholesaler:flashOffers.form.starts', 'Starts')}:</span>
                    <span className="ml-1 font-medium">{new Date(formData.startDate).toLocaleString('el-GR')}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">{t('wholesaler:flashOffers.form.ends', 'Ends')}:</span>
                    <span className="ml-1 font-medium">{new Date(formData.endDate).toLocaleString('el-GR')}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-2">{t('wholesaler:flashOffers.form.includedProducts', 'Included Products')}:</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {getSelectedProductDetails().map(p => {
                    const offerPrice = calculateOfferPrice(p.pricing.unitPrice, p._id);
                    const hasCustomPrice = formData.discountType === 'fixed_price' && customPrices[p._id] != null;
                    return (
                      <div key={p._id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                        <span className="truncate">{p.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-gray-400 line-through">€{p.pricing.unitPrice.toFixed(2)}</span>
                          <span className={`font-bold ${hasCustomPrice || formData.discountType === 'percentage' ? 'text-red-600' : 'text-gray-500'}`}>
                            €{offerPrice.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {formData.publishImmediately && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    {t('wholesaler:flashOffers.form.publishWarning', 'This offer will be published immediately and customers will be notified via email and in-app notification.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-between bg-gray-50">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                ← {t('common:back', 'Back')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-500 hover:text-gray-700">
              {t('common:cancel', 'Cancel')}
            </button>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !formData.title.trim()) || (step === 2 && selectedProducts.length === 0)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                {t('common:next', 'Next')} →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 font-medium flex items-center gap-2"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                {formData.publishImmediately
                  ? t('wholesaler:flashOffers.form.publishBtn', 'Publish Offer')
                  : t('wholesaler:flashOffers.form.saveBtn', 'Save as Draft')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// MAIN FLASH OFFERS PAGE
// ================================================================
const FlashOffers = () => {
  const { t } = useTranslation(['wholesaler', 'common']);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [expandedOffer, setExpandedOffer] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, icon: 'warning', confirmColor: 'red' });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  // Bulk selection
  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect
  } = useRangeSelect(offers);

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.status = filter;

      const res = await axios.get(`${API_URL}/flash-offers/my-offers`, {
        headers: getAuthHeaders(),
        params
      });

      setOffers(res.data.offers || []);
      setUsage(res.data.usage || null);
    } catch (err) {
      console.error('Fetch offers error:', err);
      setError(err.response?.data?.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Clear selection when offers change
  useEffect(() => {
    clearSelection();
  }, [offers, clearSelection]);

  const handleCancel = async (offerId) => {
    setConfirmModal({
      isOpen: true,
      title: t('wholesaler:flashOffers.confirmCancel', 'Are you sure you want to cancel this offer?'),
      message: t('wholesaler:flashOffers.confirmCancelDesc', 'This action cannot be undone.'),
      icon: 'warning',
      confirmLabel: t('common:confirm', 'Confirm'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await axios.post(`${API_URL}/flash-offers/${offerId}/cancel`, {}, { headers: getAuthHeaders() });
          fetchOffers();
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to cancel offer');
        }
      }
    });
  };

  const handleDelete = async (offerId) => {
    setConfirmModal({
      isOpen: true,
      title: t('wholesaler:flashOffers.confirmDelete', 'Delete this draft offer?'),
      message: t('wholesaler:flashOffers.confirmDeleteDesc', 'This offer will be permanently removed.'),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await axios.delete(`${API_URL}/flash-offers/${offerId}`, { headers: getAuthHeaders() });
          fetchOffers();
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to delete offer');
        }
      }
    });
  };

  const handlePublish = async (offerId) => {
    try {
      await axios.post(`${API_URL}/flash-offers/${offerId}/publish`, {}, { headers: getAuthHeaders() });
      fetchOffers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish offer');
    }
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    setConfirmModal({
      isOpen: true,
      title: t('wholesaler:flashOffers.bulkDeleteTitle', 'Delete / Cancel Selected Offers'),
      message: t('wholesaler:flashOffers.bulkDeleteMessage', 'This will delete {{count}} selected offers (drafts will be deleted, active/scheduled will be cancelled).', { count }),
      icon: 'danger',
      confirmLabel: t('common:confirm', 'Confirm'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await axios.delete(`${API_URL}/flash-offers/bulk`, {
            data: { ids: [...selectedIds] },
            headers: getAuthHeaders()
          });
          clearSelection();
          fetchOffers();
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to bulk delete offers');
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800',
      draft: 'bg-gray-100 text-gray-600',
      expired: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    const labels = {
      active: t('wholesaler:flashOffers.status.active', 'Active'),
      scheduled: t('wholesaler:flashOffers.status.scheduled', 'Scheduled'),
      draft: t('wholesaler:flashOffers.status.draft', 'Draft'),
      expired: t('wholesaler:flashOffers.status.expired', 'Expired'),
      cancelled: t('wholesaler:flashOffers.status.cancelled', 'Cancelled')
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatTimeRemaining = (endDate) => {
    const remaining = new Date(endDate) - new Date();
    if (remaining <= 0) return t('wholesaler:flashOffers.ended', 'Ended');
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  const canCreate = usage && (usage.limit === -1 || usage.used < usage.limit) &&
    ['professional', 'enterprise'].includes(usage.tier);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-7 h-7 text-red-500" />
              {t('wholesaler:flashOffers.title', 'Flash Offers')}
            </h1>
            <p className="text-gray-500 mt-1">
              {t('wholesaler:flashOffers.subtitle', 'Create time-limited offers to boost sales')}
            </p>
          </div>

          {canCreate && (
            <button
              onClick={() => { setEditingOffer(null); setShowModal(true); }}
              className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-orange-500 text-white px-5 py-2.5 rounded-lg hover:from-red-700 hover:to-orange-600 font-medium shadow-md shadow-red-200"
            >
              <Plus className="w-5 h-5" />
              {t('wholesaler:flashOffers.createNew', 'New Flash Offer')}
            </button>
          )}
        </div>

        {/* Usage Banner */}
        {usage && (
          <div className={`rounded-lg p-4 mb-6 flex items-center justify-between ${
            usage.tier === 'starter'
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {usage.tier === 'starter' ? (
              <div className="flex items-center gap-3">
                <ArrowUpCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {t('wholesaler:flashOffers.upgradeRequired', 'Flash Offers require Professional or Enterprise tier')}
                  </p>
                  <p className="text-sm text-amber-600">
                    {t('wholesaler:flashOffers.upgradeDesc', 'Upgrade to unlock promotional flash offers for your customers.')}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{usage.used}</p>
                    <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.usedThisMonth', 'Used')}</p>
                  </div>
                  <div className="text-gray-300 text-2xl font-light">/</div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{usage.limit === -1 ? '∞' : usage.limit}</p>
                    <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.monthlyLimit', 'Limit')}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500 capitalize">{usage.tier} {t('wholesaler:flashOffers.tierLabel', 'tier')}</span>
              </>
            )}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'active', 'scheduled', 'draft', 'expired', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {f === 'all' ? t('common:all', 'All') : getStatusBadge(f).props.children}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Offers list */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            {t('common:loading', 'Loading...')}
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Zap className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              {t('wholesaler:flashOffers.noOffers', 'No flash offers yet')}
            </h3>
            <p className="text-gray-400 mb-6">
              {t('wholesaler:flashOffers.noOffersDesc', 'Create your first flash offer to boost sales and notify your customers.')}
            </p>
            {canCreate && (
              <button
                onClick={() => { setEditingOffer(null); setShowModal(true); }}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700"
              >
                <Plus className="w-5 h-5" />
                {t('wholesaler:flashOffers.createFirst', 'Create Your First Offer')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4" {...dragProps} ref={dragProps.ref} style={{ userSelect: isDragging ? 'none' : undefined }}>
            <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
            {offers.map(offer => {
              const isSelected = selectedIds.has(offer._id);
              return (
                <div
                  key={offer._id}
                  data-select-id={offer._id}
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${
                    isSelected ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200'
                  }`}
                >
                  {/* Offer header */}
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {/* Selection checkbox */}
                        <button
                          onClick={(e) => handleItemClick(offer._id, e)}
                          className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-indigo-400'
                          }`}
                        >
                          {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(offer.status)}
                            {offer.status === 'active' && (
                              <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTimeRemaining(offer.endDate)}
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-gray-900 text-lg">{offer.title}</h3>
                          {offer.description && (
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{offer.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {offer.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handlePublish(offer._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title={t('wholesaler:flashOffers.actions.publish', 'Δημοσίευση')}
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => { setEditingOffer(offer); setShowModal(true); }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title={t('wholesaler:flashOffers.actions.edit', 'Επεξεργασία')}
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(offer._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title={t('wholesaler:flashOffers.actions.delete', 'Διαγραφή')}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {(offer.status === 'active' || offer.status === 'scheduled') && (
                          <button
                            onClick={() => handleCancel(offer._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title={t('wholesaler:flashOffers.actions.cancel', 'Ακύρωση')}
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => setExpandedOffer(expandedOffer === offer._id ? null : offer._id)}
                          className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg"
                        >
                          {expandedOffer === offer._id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-6 mt-3 text-sm ml-8">
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Tag className="w-4 h-4" />
                        <span>
                          {offer.discountType === 'percentage' ? `${offer.discountPercentage}%` : 'Custom'} {t('wholesaler:flashOffers.off', 'έκπτωση')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Package className="w-4 h-4" />
                        <span>{offer.items?.length || 0} {t('wholesaler:flashOffers.products', 'products')}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(offer.startDate).toLocaleDateString('el-GR')} – {new Date(offer.endDate).toLocaleDateString('el-GR')}</span>
                      </div>
                      {(offer.stats?.views > 0 || offer.stats?.ordersGenerated > 0) && (
                        <>
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <Eye className="w-4 h-4" />
                            <span>{offer.stats.views}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-green-600">
                            <ShoppingCart className="w-4 h-4" />
                            <span>{offer.stats.ordersGenerated} {t('wholesaler:flashOffers.orders', 'orders')}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded section — product list */}
                  {expandedOffer === offer._id && (
                    <div className="border-t bg-gray-50 p-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">{t('wholesaler:flashOffers.includedProducts', 'Included Products')}:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {offer.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-gray-100">
                            <div className="flex items-center gap-2 min-w-0">
                              {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded object-cover" />}
                              <span className="text-sm truncate">{item.productName}</span>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <span className="text-xs text-gray-400 line-through">€{item.originalPrice?.toFixed(2)}</span>
                              <span className="ml-1 text-sm font-bold text-red-600">€{item.offerPrice?.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {offer.stats && (offer.stats.views > 0 || offer.stats.ordersGenerated > 0) && (
                        <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-lg font-bold text-gray-900">{offer.stats.views}</p>
                            <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.analytics.views', 'Views')}</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{offer.stats.uniqueViews}</p>
                            <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.analytics.unique', 'Unique')}</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-green-600">{offer.stats.ordersGenerated}</p>
                            <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.analytics.orders', 'Orders')}</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-green-600">€{(offer.stats.revenueGenerated || 0).toFixed(0)}</p>
                            <p className="text-xs text-gray-500">{t('wholesaler:flashOffers.analytics.revenue', 'Revenue')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Action Bar for bulk actions */}
      <FloatingActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onSelectAll={selectAll}
      >
        <button
          onClick={handleBulkDelete}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          {t('wholesaler:flashOffers.bulkDeleteBtn', 'Delete / Cancel Selected')}
        </button>
      </FloatingActionBar>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <FlashOfferModal
          offer={editingOffer}
          onClose={() => { setShowModal(false); setEditingOffer(null); }}
          onSave={fetchOffers}
        />
      )}

      <Footer />
    </div>
  );
};

export default FlashOffers;
